import { Box, InputAdornment, type SxProps, TextField, type Theme } from '@mui/material';
import { Field, type FieldAttributes, useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { DateTime, Duration } from 'luxon';
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NumericFormat } from 'react-number-format';

import type Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import { FormikTel, SetFormikValue } from '@core/utils/formikHelpers';
import { getUnitLabel } from '@core/utils/unitHelpers';

import { DatePicker } from '../DatePicker/DatePicker';
import { DateTimePicker } from '../DateTimePicker/DateTimePicker';
import { InputTooltip } from '../InputTooltip/InputTooltip';

type InputTypeOptions =
  | 'text'
  | 'number'
  | 'textarea'
  | 'date'
  | 'datetime-local'
  | 'datetime'
  | 'email'
  | 'tel'
  | 'duration-hour-minute'
  | 'currency'
  | 'cost';

// Cost inputs might contain `{ unit: 'Required' }` errors. When this happens,
// if we do not handle it appropriately (see below), there is a runtime panic.
const costErrorHelperText = (formikError: string | { unit: Enums.Unit | undefined }): string => {
  if (typeof formikError === 'string') {
    return formikError;
  }
  if (formikError.unit === undefined) {
    return ' ';
  }
  const label = getUnitLabel(formikError.unit);
  return label !== '' ? `Unit: ${label}` : `Unit: ${formikError.unit}`;
};

// Helper to safely convert any error to a string for display
const safeErrorHelperText = (formikError: unknown): string => {
  if (typeof formikError === 'string') {
    return formikError;
  }
  // If error is an object (e.g., {firstName: 'Required', lastName: 'Required'}),
  // extract the first error message to display
  if (typeof formikError === 'object' && formikError !== null) {
    const firstError = Object.values(formikError)[0];
    return typeof firstError === 'string' ? firstError : ' ';
  }
  return ' ';
};

/** Floor applied to `type='date'` inputs when no `min` is given, to reject typos like `03/31/0007`. */
const MIN_DATE_INPUT_VALUE = '1900-01-01';

export type InputProps = {
  label?: string;
  name: string;

  /** Default 'text' */
  type?: InputTypeOptions;
  /** Default empty */
  placeholder?: string;
  disabled?: boolean;
  /**
   * Min numbers should usually use Yup validation.
   * For `type='date'`, defaults to '1900-01-01' when omitted.
   */
  min?: string;
  /** Max numbers should use usually Yup validation. */
  max?: string;
  /** Max digits allowed before the decimal point for numeric inputs. */
  maxIntegerDigits?: number;
  /** Granularity of the minutes column for `type='datetime'`, in minutes. Default 1. */
  minutesStep?: number;
  /** Optional onChange chain function */
  onInputChange?: (arg0: string) => void | Promise<void>;
  /** value to prepend to input */
  startAdornment?: string;
  /** value to append to input */
  endAdornment?: string | ReactElement;
  /** number of rows for a textarea input */
  rows?: number;
  tip?: string;
  /**
   * Optional limit to trailing decimal places.
   * @default ['cost', 'currency'].includes(type) ? 2 : undefined
   */
  precision?: number;
  /** Whether negative numbers are allowed for numeric input types. Default true. */
  allowNegative?: boolean;
  sx?: SxProps<Theme>;
  readOnly?: boolean;
  /**
   * When true, the helper text area is not rendered when there is no error. Default false.
   *
   * This property exists to match the logic used in the figma design and gradually move the UI towards it.
   * */
  omitEmptyHelperText?: boolean;
};

/**
 * Given an input value of any type, convert it to the correct input value string
 */
const handleValue = (value: string | null | undefined, type: InputTypeOptions): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  switch (type) {
    case 'date':
      return DateTime.fromISO(value).toISODate() ?? '';
    case 'datetime-local':
    case 'datetime':
      // The yyyy-MM-ddThh:mm format is required by the datetime-local <input> type
      return (
        DateTime.fromISO(value).startOf('minute').toISO({
          includeOffset: false,
          suppressSeconds: true,
          suppressMilliseconds: true,
        }) ?? ''
      );
    case 'duration-hour-minute':
      return Duration.fromISO(value).toFormat("h' hours' m' minutes'");
    case 'tel':
      return FormikTel(value);
    case 'cost':
    case 'currency':
    case 'email':
    case 'number':
    case 'text':
    case 'textarea':
    default:
      return value;
  }
};

export const Input = ({
  label,
  name,
  type = 'text',
  placeholder,
  disabled,
  min,
  max,
  maxIntegerDigits,
  minutesStep,
  onInputChange,
  startAdornment,
  endAdornment,
  rows,
  tip,
  precision = ['cost', 'currency'].includes(type) ? 4 : undefined,
  allowNegative = true,
  sx,
  readOnly = false,
  omitEmptyHelperText = false,
}: InputProps): ReactElement => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const formikBag = useFormikContext<any>();

  const formikName = ((): string => {
    switch (type) {
      case 'cost':
        return `${name}.amount.number`;
      case 'currency':
        return `${name}.number`;
      case 'date':
      case 'datetime-local':
      case 'datetime':
      case 'duration-hour-minute':
      case 'email':
      case 'number':
      case 'tel':
      case 'text':
      case 'textarea':
      default:
        return name;
    }
  })();

  const [localValue, setLocalValue] = useState<string | undefined>();
  const { value: fieldValue } = formikBag.getFieldProps(formikName);
  const isExternallySet = useRef(false);

  // Sync localValue with formikBag value on initial render
  useEffect(() => {
    if (!isExternallySet.current && localValue !== handleValue(fieldValue, type)) {
      setLocalValue(handleValue(fieldValue, type));
    }
    isExternallySet.current = false; // Reset after external sync
  }, [fieldValue, type]);

  // The debounced call runs 300ms after the keystroke; reading the latest callback then keeps it
  // from writing a form snapshot that predates another field's edit.
  const latestRef = useRef({ formikBag, onInputChange });
  useEffect(() => {
    latestRef.current = { formikBag, onInputChange };
  });

  // updateFormikValue is debounced to prevent rapid changes from causing unnecessary re-renders.
  const updateFormikValue = useMemo(
    () =>
      debounce(async (newValue: string) => {
        let cleanValue = newValue;
        if (type === 'number') {
          cleanValue = newValue.replaceAll(',', '');
        }
        latestRef.current.formikBag.setFieldValue(formikName, cleanValue);
        await latestRef.current.onInputChange?.(cleanValue.replaceAll(',', ''));
      }, 300),
    [type, formikName],
  );

  // handleInputChange is called whenever the input value changes.
  // It updates the local value and calls updateFormikValue to update the formik value
  // in a debounced manner, particularly the `onInputChange` that likely effects other
  // inputs in a form state.
  const handleInputChange = (value: string | null): void => {
    setLocalValue(value || '');
    isExternallySet.current = true;
    updateFormikValue(value || '');
  };
  const handleInputChangeEvent = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    handleInputChange(handleValue(e.target.value, type));
  };

  return (
    <Field id={name} name={formikName}>
      {({ field: { value, onBlur }, meta }: FieldAttributes<any>): ReactElement => {
        const formikBagMeta = formikBag.getFieldMeta(name);
        const hasError: boolean =
          formikBagMeta.touched === true && formikBagMeta.error !== undefined;

        // If the base object reference is passed as name, the error could be at a lower level or the highest level.
        const formikError = meta.error ?? formikBagMeta.error;

        // A Save clicked inside the debounce must still submit the typed value. Flush after onBlur,
        // whose validation reads last render's values, so the flushed value's validation lands last.
        const blurThenFlush = async (e: FocusEvent<HTMLElement>): Promise<void> => {
          await onBlur(e);
          await updateFormikValue.flush();
        };

        const labelWithTip =
          label === undefined ? null : (
            <Box display='flex' gap='0.25rem' alignItems='center'>
              {label}
              {tip !== undefined ? <InputTooltip tip={tip} /> : null}
            </Box>
          );

        let helperText = omitEmptyHelperText ? undefined : ' ';

        switch (type) {
          case 'number':
          case 'currency':
          case 'cost':
            if (hasError) {
              helperText = costErrorHelperText(formikError);
            }

            return (
              <NumericFormat
                id={formikName}
                decimalScale={precision}
                thousandSeparator
                allowNegative={allowNegative}
                isAllowed={(values): boolean => {
                  if (maxIntegerDigits === undefined) {
                    return true;
                  }
                  const rawValue = values.value ?? '';
                  if (rawValue === '') {
                    return true;
                  }
                  const normalizedValue = rawValue.startsWith('-') ? rawValue.slice(1) : rawValue;

                  const [integerPart] = normalizedValue.split('.');
                  return integerPart.length <= maxIntegerDigits;
                }}
                customInput={TextField}
                InputProps={{
                  readOnly,
                  ...(readOnly ? { sx: { pointerEvents: 'none' } } : {}),
                  startAdornment:
                    startAdornment !== undefined ? (
                      <InputAdornment position='start'>{startAdornment}</InputAdornment>
                    ) : undefined,
                  endAdornment:
                    endAdornment !== undefined ? (
                      <InputAdornment position='end'>{endAdornment}</InputAdornment>
                    ) : undefined,
                }}
                name={formikName}
                label={labelWithTip}
                value={localValue ?? handleValue(value, type)}
                onBlur={async (e): Promise<void> => {
                  // If you enter a negative then click away, NumberFormat automatically clears it.
                  // Because of that, we check and if it is only a `-`, set the formikBag state to match the
                  // soon-to-be empty string. Otherwise an `Invalid` error is displayed, while no value exists.
                  // Read before awaiting: React clears currentTarget once the event has dispatched.
                  const curV = e.currentTarget.value;
                  await onBlur(e);
                  if (curV === '-') {
                    updateFormikValue.cancel();
                    await SetFormikValue(formikBag, formikName, '');
                  } else {
                    await updateFormikValue.flush();
                  }
                }}
                onChange={handleInputChangeEvent}
                fullWidth
                error={hasError}
                helperText={helperText}
                placeholder={placeholder}
                disabled={disabled}
                // Close outlined box + fix height if label is undefined
                sx={
                  !hasValue(label)
                    ? { fieldset: { top: 0 }, legend: { display: 'none' }, ...sx }
                    : sx
                }
              />
            );
          case 'date':
            if (hasError) {
              helperText = safeErrorHelperText(formikError);
            }
            return (
              <DatePicker
                readOnly={readOnly}
                inputRef={textFieldRef}
                id={formikName}
                name={formikName}
                label={labelWithTip}
                value={localValue ?? handleValue(value, type)}
                onChange={handleInputChange}
                onBlur={blurThenFlush}
                minDate={min ?? MIN_DATE_INPUT_VALUE}
                maxDate={max}
                fullWidth
                error={hasError}
                helperText={helperText}
                placeholder={placeholder}
                disabled={disabled}
                // Default 'shrink' date labels because they overlap the select text
                // Close outlined box + fix height if label is undefined
                sx={
                  !hasValue(label)
                    ? { fieldset: { top: 0 }, legend: { display: 'none' }, ...sx }
                    : sx
                }
              />
            );

          case 'datetime':
            if (hasError) {
              helperText = safeErrorHelperText(formikError);
            }
            return (
              <DateTimePicker
                readOnly={readOnly}
                inputRef={textFieldRef}
                id={formikName}
                name={formikName}
                label={labelWithTip}
                value={localValue ?? handleValue(value, type)}
                onChange={handleInputChange}
                onBlur={blurThenFlush}
                minDateTime={min}
                maxDateTime={max}
                minutesStep={minutesStep}
                fullWidth
                error={hasError}
                helperText={helperText}
                placeholder={placeholder}
                disabled={disabled}
                // Close outlined box + fix height if label is undefined
                sx={
                  !hasValue(label)
                    ? { fieldset: { top: 0 }, legend: { display: 'none' }, ...sx }
                    : sx
                }
              />
            );

          case 'tel':
          case 'email':
          case 'datetime-local':
          case 'duration-hour-minute':
          case 'textarea':
          case 'text':
            if (hasError) {
              helperText = safeErrorHelperText(formikError);
            }
            return (
              <TextField
                inputRef={textFieldRef}
                id={formikName}
                name={formikName}
                label={labelWithTip}
                value={localValue ?? handleValue(value, type)}
                onBlur={blurThenFlush}
                onChange={handleInputChangeEvent}
                type={type}
                onClick={(): void => {
                  if (type === 'datetime-local' && !readOnly) {
                    textFieldRef.current?.showPicker?.();
                    textFieldRef.current?.focus();
                  }
                }}
                inputProps={{
                  min,
                  max,
                }}
                // min/max allowed on inputProps but not InputProps.
                // adornments on inputProps are not respected.
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  ...(readOnly ? { sx: { pointerEvents: 'none' } } : {}),
                  readOnly,
                  startAdornment:
                    startAdornment !== undefined ? (
                      <InputAdornment position='start'>{startAdornment}</InputAdornment>
                    ) : undefined,
                  endAdornment:
                    endAdornment !== undefined ? (
                      <InputAdornment position='end'>{endAdornment}</InputAdornment>
                    ) : undefined,
                }}
                fullWidth
                multiline={type === 'textarea'}
                rows={rows}
                error={hasError}
                helperText={helperText}
                placeholder={placeholder}
                disabled={disabled}
                // Default 'shrink' date labels because they overlap the select text
                InputLabelProps={
                  type === 'datetime-local'
                    ? {
                        shrink: true,
                      }
                    : {}
                }
                // Close outlined box + fix height if label is undefined
                sx={
                  !hasValue(label)
                    ? { fieldset: { top: 0 }, legend: { display: 'none' }, ...sx }
                    : sx
                }
              />
            );

          // This default state will never be reached.
          default:
            return <div />;
        }
      }}
    </Field>
  );
};
